import type {
	HouseholdConsumptionState,
	HouseholdItem,
} from "./household-consumption";
import { itemCadenceDays } from "./household-consumption";

export interface RestockForecast {
	itemId: string;
	name: string;
	dueAt: string;
	daysLeft: number;
	cadenceDays: number;
}

const DAY = 86_400_000;

/**
 * #32: items expected to run out within `horizonDays`. The cadence comes from
 * real purchase dates (two or more receipts) or, failing that, the measured
 * open→finished cycles. Items bought fewer than twice are never predicted.
 */
export function forecastRestock(
	items: HouseholdItem[],
	receipts: {
		receiptId: string;
		transactedAt: string;
		deletedAt?: string | null;
	}[],
	now = new Date(),
	horizonDays = 7,
): RestockForecast[] {
	const dateOf = new Map(
		receipts
			.filter((receipt) => !receipt.deletedAt)
			.map((receipt) => [receipt.receiptId, Date.parse(receipt.transactedAt)]),
	);
	const results: RestockForecast[] = [];
	for (const item of items) {
		if (item.status !== "in_stock" || item.avoidNext) continue;
		const purchases = (item.sourceReceiptIds ?? [])
			.map((id) => dateOf.get(id))
			.filter(
				(time): time is number =>
					typeof time === "number" && Number.isFinite(time),
			)
			.sort((a, b) => a - b);
		let cadence: number | null = null;
		if (purchases.length >= 2) {
			const gaps = purchases
				.slice(1)
				.map((time, index) => time - (purchases[index] ?? time));
			cadence = Math.round(
				gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length / DAY,
			);
		} else if (item.purchaseCount >= 2) cadence = itemCadenceDays(item);
		if (!cadence || cadence < 1) continue;
		const last =
			purchases.at(-1) ??
			(item.lastPurchasedAt ? Date.parse(item.lastPurchasedAt) : Number.NaN);
		if (!Number.isFinite(last)) continue;
		const due = last + cadence * DAY;
		const daysLeft = Math.ceil((due - now.getTime()) / DAY);
		if (daysLeft > horizonDays) continue;
		results.push({
			itemId: item.itemId,
			name: item.name,
			dueAt: new Date(due).toISOString(),
			daysLeft,
			cadenceDays: cadence,
		});
	}
	return results.sort((a, b) => a.daysLeft - b.daysLeft);
}

/** #32: one tap from "곧 떨어질 물건" into the pending shopping list. */
export function queueRestock(
	state: HouseholdConsumptionState,
	input: { itemId: string; name: string; at: string; entryId: string },
): HouseholdConsumptionState {
	if (
		state.shopping.some(
			(entry) => entry.itemId === input.itemId && entry.status === "pending",
		)
	)
		return state;
	return {
		...state,
		shopping: [
			...state.shopping,
			{
				entryId: input.entryId,
				itemId: input.itemId,
				name: input.name,
				status: "pending",
				source: "low",
				addedAt: input.at,
				purchasedAt: null,
				purchasedReceiptId: null,
			},
		],
	};
}
